import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { useId, useMemo } from 'react';
import { Link } from '#components/Link/Link';
import { useBreakpoint } from '#cutils/hooks/useBreakpoint';
import type { NavigationList } from '#cutils/page-context/navigation';
import { IconSystem } from '#icons/IconSystem';
import styles from './HeaderNavigation.module.scss';
import { HighlightNavigation } from './HighlightNavigation';
import { Logo } from './Logo';
import { ScrollDirection, useVerticalScrollDirection } from './use-vertical-scroll-position';
type Props = {
  className?: string;
  menuOpen: boolean;
  navigationTitle: string;
  onMainMenuClick?: () => void;
  onBayernMenuClick?: () => void;
  menuButtonRef: RefObject<HTMLButtonElement | undefined>;
  bayernButtonRef: RefObject<HTMLButtonElement | undefined>;
  highlightNavigation: NavigationList;
  hideBayernMenu: boolean;
};
export function HeaderNavigation(t0) {
  const $ = _c(41);
  const {
    className,
    menuOpen,
    onMainMenuClick,
    onBayernMenuClick,
    menuButtonRef,
    bayernButtonRef,
    navigationTitle,
    highlightNavigation,
    hideBayernMenu
  } = t0;
  const isDesktop = useBreakpoint("lg");
  const direction = useVerticalScrollDirection(20, isDesktop);
  const searchLinkId = useId();
  const menuButtonId = useId();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className={styles.buttonCaption}>Suche</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isDesktop ? undefined : "Suche";
  const t3 = isDesktop ? searchLinkId : undefined;
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = <IconSystem className={styles.navigationIcon} icon="search" variant="filled" title={t2} aria-labelledby={t3} />;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== searchLinkId || $[5] !== t4) {
    t5 = <Link href="/suche" className={`${styles.navigationButton} ${styles.search}`} id={searchLinkId}>{t1}{t4}</Link>;
    $[4] = searchLinkId;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const search = t5;
  const isCondensed = direction === ScrollDirection.Down;
  let t6;
  if ($[7] !== menuButtonRef) {
    t6 = function handleMenuButtonRef(element) {
      menuButtonRef.current = element;
    };
    $[7] = menuButtonRef;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleMenuButtonRef = t6;
  const t7 = `${styles.navigationContainer} ${className ?? ""} ${menuOpen ? styles.menuOpen : ""} ${isDesktop && isCondensed ? styles.condensed : ""} animate-hover`;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Logo className={styles.logo} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== bayernButtonRef || $[11] !== hideBayernMenu || $[12] !== highlightNavigation || $[13] !== onBayernMenuClick) {
    t9 = <HighlightNavigation className={styles.highlightNavigation} highlightNavigation={highlightNavigation} onBayernMenuClick={onBayernMenuClick} bayernButtonRef={bayernButtonRef} hideBayernMenu={hideBayernMenu} />;
    $[10] = bayernButtonRef;
    $[11] = hideBayernMenu;
    $[12] = highlightNavigation;
    $[13] = onBayernMenuClick;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== navigationTitle) {
    t10 = <div className={`heading4 ${styles.title}`}>{navigationTitle}</div>;
    $[15] = navigationTitle;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = menuOpen ? "Men\xFC schlie\xDFen" : "Men\xFC \xF6ffnen";
  let t12;
  if ($[17] !== onMainMenuClick) {
    t12 = () => onMainMenuClick?.();
    $[17] = onMainMenuClick;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = menuOpen ? "schlie\xDFen" : "Men\xFC";
  let t14;
  if ($[19] !== t13) {
    t14 = <span className={styles.buttonCaption}>{t13}</span>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== isDesktop || $[22] !== menuButtonId || $[23] !== menuOpen) {
    t15 = menuOpen ? <IconSystem icon="close" variant="filled" className={styles.navigationIcon} title={isDesktop ? undefined : "schlie\xDFen"} aria-labelledby={isDesktop ? menuButtonId : undefined} /> : <IconSystem icon="menu" variant="filled" className={styles.navigationIcon} title={isDesktop ? undefined : "Navigationsmen\xFC"} aria-labelledby={isDesktop ? menuButtonId : undefined} />;
    $[21] = isDesktop;
    $[22] = menuButtonId;
    $[23] = menuOpen;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== handleMenuButtonRef || $[26] !== menuButtonId || $[27] !== menuOpen || $[28] !== t11 || $[29] !== t12 || $[30] !== t14 || $[31] !== t15) {
    t16 = <button className={`heading4 ${styles.navigationButton} button-link`} title={t11} onClickCapture={t12} aria-haspopup="menu" aria-expanded={menuOpen} ref={handleMenuButtonRef} id={menuButtonId}>{t14}{t15}</button>;
    $[25] = handleMenuButtonRef;
    $[26] = menuButtonId;
    $[27] = menuOpen;
    $[28] = t11;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== search || $[34] !== t16) {
    t17 = <div className={styles.navigationButtons}>{search}{t16}</div>;
    $[33] = search;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t10 || $[37] !== t17 || $[38] !== t7 || $[39] !== t9) {
    t18 = <div className={t7}>{t8}{t9}{t10}{t17}</div>;
    $[36] = t10;
    $[37] = t17;
    $[38] = t7;
    $[39] = t9;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}